// How a port-state answer names its ports, and what a reader that names a
// port by a configuration's word may take from one that does not (#1429,
// ADR-0266).
//
// `GET /switches/{serial}/ports/state` answers `keyed_by: document` for a
// switch whose cell holds a configuration document: the ports are that
// document's. For a switch whose cell holds none it answers `keyed_by:
// report`: the ports are the keys of the switch's own report, carried exactly
// as the switch spelt them and resolved against nothing.
//
// **A report key is not a port of any configuration.** Every reader but one
// names a port by a configuration's word: a declared link's end, a port
// intent, a catalogue model's port, a way out, the port a faceplate draws.
// Matching one of those to a report key is the translation §11.3 forbids,
// even where the two are spelt alike, so those readers take the answer
// through `documentKeyed`, which is what they were answered before the report
// was read at all: no ports, and `no_configuration` for the whole switch. Two
// readers show report keys, and neither matches one to anything. The switch's
// own Ports page (`Ports.tsx`, `ReportKeyedNotice`) lists them as the report's
// and says they are unresolved. The site's drawing (`Topology.tsx`, ADR-0351)
// takes `reportedNeighbours`: a line from the switch to each neighbour its
// report names, matched by MAC as the API matched it, labelled with the key as
// written and meeting the switch rather than a port of its face.
//
// ADR-0266 decision 2 is what `keyed_by: report` means, and decision 3 is that
// a report key is never compared with a declared port name. The copy that
// says so to an operator is in `Ports.tsx`, under its own marker.
import type { components } from "../../api/schema";

type LiveSet = components["schemas"]["SwitchPortStateSet"];

// One reduced answer per answer read, so a reader that keeps the set in an
// effect's dependencies sees the same object for the same read.
const reduced = new WeakMap<LiveSet, LiveSet>();

/** The answer as a reader that names ports by a configuration's word must
 * take it. A document-keyed answer is returned as it is. A report-keyed one
 * is returned with no ports, no report time and no PoE, and with
 * `no_configuration` for the whole switch, whatever its report said. */
export function documentKeyed(set: LiveSet): LiveSet {
  if (set.keyed_by !== "report") return set;
  let out = reduced.get(set);
  if (!out) {
    out = { site_id: set.site_id, serial: set.serial, keyed_by: "report", ports: [], unanswered: { reason: "no_configuration" } };
    reduced.set(set, out);
    // Reducing twice is reducing once.
    reduced.set(out, out);
  }
  return out;
}

/** A read of `ports/state` with its answer taken through `documentKeyed`:
 * the read's own state, reload and problem are untouched. */
export function documentKeyedRead<Q extends { data?: LiveSet }>(q: Q): Q {
  return q.data && q.data.keyed_by === "report" ? { ...q, data: documentKeyed(q.data) } : q;
}

type Neighbour = components["schemas"]["SwitchPortNeighbour"];

/** A neighbour a report-keyed answer names, and every key of the report that
 * names it (ADR-0351 decision 1). The keys are the report's as written, in
 * its order: two keys for one neighbour are one physical port spelt two ways
 * or two cables, and nothing here says which. */
export interface ReportedNeighbour { keys: string[]; neighbour: Neighbour }

const named = new WeakMap<LiveSet, ReportedNeighbour[]>();

/** The neighbours a report-keyed answer names (ADR-0351), for the drawing
 * alone: a device of the site once, by its serial, with every key naming it,
 * and a neighbour no device of the site matches once per key, as ADR-0336
 * names it on a port. Undefined for a document-keyed answer and for a switch
 * that was not read (`unanswered` for the whole switch), so an offline switch
 * is drawn with no reported neighbour and says why, as before. A key is never
 * resolved here and is matched to no port. */
export function reportedNeighbours(set: LiveSet | undefined): ReportedNeighbour[] | undefined {
  if (!set || set.keyed_by !== "report" || set.unanswered) return undefined;
  let out = named.get(set);
  if (!out) {
    out = [];
    const bySerial = new Map<string, ReportedNeighbour>();
    for (const p of set.ports) {
      const n = p.neighbour;
      if (!n) continue;
      if (n.in_site && n.serial) {
        const have = bySerial.get(n.serial);
        if (have) { have.keys.push(p.port); continue; }
        const r = { keys: [p.port], neighbour: n };
        bySerial.set(n.serial, r);
        out.push(r);
      } else out.push({ keys: [p.port], neighbour: n });
    }
    named.set(set, out);
  }
  return out;
}
